import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RequestStatus } from '../../../config/store';
import useMembers from '../../../hooks/useMembers';
import { RootState } from '../../../store/rootStore';
import useLicenses from '../../subscriptions/hooks/useLicenses';
import { getUsers, getUsersProjectCount } from '../store/users.store';
import { UserData } from '../types/user';

const useUsers = (
  only?: string[]
): {
  users: UserData[];
  usersWithLicense: UserData[];
  usersWithoutLicense: UserData[];
  isLoading: boolean;
} => {
  const dispatch = useDispatch<any>();
  const { licenses, isLoading: licenseIsLoading } = useLicenses();
  const { uniqueUserIds, members, token } = useMembers();
  const fetchedUsers = useSelector((state: RootState) => state.users.users.data || {});
  const projectCounts = useSelector((state: RootState) => state.users.userProjectCounts.data || []);
  const userStatus = useSelector((state: RootState) => state.users.users.request.get.status);
  const isLoading = React.useMemo(
    () => [RequestStatus.Pending, RequestStatus.Unsent].includes(userStatus),
    [userStatus]
  );
  React.useEffect(() => {
    const notFetchedUsers = uniqueUserIds
      .filter((id) => {
        if (only && !only.includes(id)) {
          return false;
        }
        return !fetchedUsers[id];
      })
      .filter((i) => !!i);
    if (!notFetchedUsers.length || userStatus === RequestStatus.Pending || userStatus === RequestStatus.Failure) {
      return;
    }
    dispatch(getUsers({ userIds: notFetchedUsers, token }));
    dispatch(getUsersProjectCount({ userIds: notFetchedUsers, token }));
  }, [uniqueUserIds, fetchedUsers, only, userStatus, dispatch, token]);
  const users = React.useMemo(() => {
    return Object.values(fetchedUsers)
      .map((user) => {
        const projectCount = projectCounts.find((c) => c.user_id === user.id);
        const license = licenses.find((license) => license.assigned_to === user.id);
        return {
          ...user,
          license,
          memberships: members.filter((m) => m.user_id === user.id),
          projectCount: projectCount && projectCount.projects_count,
          full_name: `${user.first_name || ''}${user.name ? ` ${user.name}` : ''}`,
        };
      })
      .filter((user) => user.memberships.length) as UserData[];
  }, [fetchedUsers, projectCounts, licenses, members]);

  const { usersWithLicense, usersWithoutLicense } = React.useMemo(
    () => ({
      usersWithLicense: users.filter((u) => !!u.license),
      usersWithoutLicense: users.filter((u) => !u.license),
    }),
    [users]
  );
  return { users, isLoading: isLoading || licenseIsLoading, usersWithLicense, usersWithoutLicense };
};

export default useUsers;
